import { DialogDisclosure, Heading } from "@ariakit/react";
import {
  DialogTrigger,
  Failure,
  ListItem,
  ListItemContainer,
  ListItemContent,
  ListItemHeading,
  ListItemSummary,
  ListView,
  Success,
} from "@indietabletop/appkit";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { groupBy } from "es-toolkit";
import {
  cloneElement,
  Fragment,
  type ReactElement,
  type ReactNode,
} from "react";
import { href } from "../App/href.tsx";
import { definition } from "../common.css.ts";
import {
  DialogContent,
  DialogHeader,
  ScrollableDialog,
} from "../Dialog/Dialog.tsx";
import { ArmorProfile } from "../engine/ArmorProfile.ts";
import { ItemProfile } from "../engine/ItemProfile.ts";
import { ShieldProfile } from "../engine/ShieldProfile.ts";
import { UpgradeProfile } from "../engine/UpgradeProfile.ts";
import { WeaponProfile } from "../engine/WeaponProfile.ts";
import { FailureView } from "../FailureView/FailureView.tsx";
import {
  armorRoll,
  attackAction,
  defenseOption,
  effect,
  itemType,
  moveAction,
  upgradeType,
  weaponType,
} from "../fmt.ts";
import type { ItemNotFoundFailure } from "../oss/Lookup.ts";
import dividerSrc from "./divider.svg";
import { KnightContext, useKnight } from "./KnightContext.tsx";
import { EditKnightDialog } from "./KnightFormDialog.tsx";
import { useIsReadOnly } from "./ReadOnlyContext.tsx";
import { definitionDialog, header, summary, view } from "./style.css.ts";
import { useKnightResult } from "./useWarbandContext.ts";
import { KnightIssues } from "./ValidationIssues.tsx";

function KnightSummaryRow(props: { name: string; children: ReactNode }) {
  const { name, children } = props;

  return (
    <div className={summary.row}>
      <div className={summary.label}>{name}</div>
      <div className={summary.value}>{children}</div>
    </div>
  );
}

function renderList<T>(format: (item: T) => string) {
  return (item: T, index: number) => [index !== 0 && ", ", format(item)];
}

function KnightSummary() {
  const knight = useKnight();

  const usableItems = knight.items
    .filter((lookup) => lookup.isSuccess)
    .map((lookup) => lookup.value)
    .filter((item) => item.type !== "WORN");

  return (
    <div className={summary.container}>
      <KnightSummaryRow name="Attacks">
        <p className={summary.special}>
          {knight.attackActions.map(renderList(attackAction))}
        </p>

        {knight.attackSpecialRules.map((r, i) => {
          return (
            <p
              key={i}
              className={summary.special}
              style={{ fontStyle: "italic" }}
            >
              {r.rule}
            </p>
          );
        })}
      </KnightSummaryRow>

      <KnightSummaryRow name="Defense">
        <p className={summary.special}>
          {knight.defenseOptions.map(renderList(defenseOption))}
        </p>

        {knight.defenseSpecialRules.map((r, i) => {
          return (
            <p
              key={i}
              className={summary.special}
              style={{ fontStyle: "italic" }}
            >
              {r.rule}
            </p>
          );
        })}
      </KnightSummaryRow>

      <KnightSummaryRow name="Armor">
        <p className={summary.special}>
          {knight.armorRolls.map(renderList(armorRoll))}
        </p>

        {knight.armorSpecialRules.map((r, i) => {
          return (
            <p
              key={i}
              className={summary.special}
              style={{ fontStyle: "italic" }}
            >
              {r.rule}
            </p>
          );
        })}
      </KnightSummaryRow>

      <KnightSummaryRow name="Move">
        <p className={summary.special}>
          {knight.moveActions.map(renderList(moveAction))}
        </p>
      </KnightSummaryRow>

      <KnightSummaryRow name="Usable">
        {!!usableItems.length && (
          <p className={summary.special}>
            {usableItems.map(renderList((item) => item.name))}
          </p>
        )}
      </KnightSummaryRow>

      <KnightSummaryRow name="Passive">
        {knight.passiveRules.map((item, index) => (
          <p
            key={index}
            className={summary.special}
            style={{ fontStyle: "italic" }}
          >
            {item.rule}
          </p>
        ))}
      </KnightSummaryRow>
    </div>
  );
}

function groupLookups<T extends Success<unknown>>(
  items: (T | Failure<ItemNotFoundFailure>)[],
) {
  const groups = groupBy(items, (lookup) =>
    lookup.isSuccess ? "found" : "missing",
  );

  return groups as { found?: T[]; missing?: Failure<ItemNotFoundFailure>[] };
}

function EquipmentListItem(props: {
  heading: string;
  type: string;
  summary: ReactNode;
}) {
  return (
    <ListItemContainer>
      <ListItemContent>
        <ListItemHeading className={view.listItemHeading}>
          {props.heading}
        </ListItemHeading>
        <ListItemSummary className={view.listItemSummary}>
          <em>{props.type}</em>
          {props.summary}
        </ListItemSummary>
      </ListItemContent>
    </ListItemContainer>
  );
}

function KnightEquipment() {
  const knight = useKnight();
  const groups = groupLookups(knight.equipment);

  return (
    <div>
      <Heading className={view.subheading}>Equipment</Heading>

      <ListView fallback="This knight has no equipment." className={view.list}>
        {groups.found?.map((lookup) => {
          const item = lookup.value;

          if (item instanceof ArmorProfile) {
            return (
              <EquipmentListItem
                key={item.id}
                heading={item.name}
                type="Armor"
                summary={item.summary}
              />
            );
          }

          if (item instanceof WeaponProfile) {
            return (
              <EquipmentListItem
                key={item.id}
                heading={item.name}
                type={weaponType(item.type)}
                summary={
                  <>
                    {item.traits
                      .filter((lookup) => lookup.isSuccess)
                      .map((lookup, index) => {
                        const trait = lookup.value;
                        return (
                          <Fragment key={trait.id}>
                            {index !== 0 && " "}
                            <DialogTrigger key={trait.id}>
                              <ScrollableDialog isNarrow>
                                <DialogHeader heading={trait.name} />
                                <DialogContent>
                                  <p className={definitionDialog.paragraph}>
                                    {trait.description}
                                  </p>
                                </DialogContent>
                              </ScrollableDialog>

                              <DialogDisclosure className={definition}>
                                {trait.name}.
                              </DialogDisclosure>
                            </DialogTrigger>
                          </Fragment>
                        );
                      })}{" "}
                    {item.effects.map(effect).join(" ")}
                  </>
                }
              />
            );
          }

          if (item instanceof ShieldProfile) {
            return (
              <EquipmentListItem
                key={item.id}
                heading={item.name}
                type="Shield"
                summary={item.summary}
              />
            );
          }

          if (item instanceof ItemProfile) {
            return (
              <EquipmentListItem
                key={item.id}
                heading={item.name}
                type={itemType(item.type)}
                summary={item.summary}
              />
            );
          }

          if (item instanceof UpgradeProfile) {
            return (
              <EquipmentListItem
                key={item.id}
                heading={item.name}
                type={upgradeType(item.type)}
                summary={item.summary}
              />
            );
          }

          return null;
        })}

        {groups.missing?.map((lookup) => {
          const key = lookup.failure.missingKey;
          return (
            <ListItem
              key={key}
              heading="Not found"
              summary={`Missing item at key ${key}`}
            />
          );
        })}
      </ListView>
    </div>
  );
}

type HeaderVariants = RecipeVariants<typeof header.row>;

function StatIndicator(
  props: HeaderVariants & {
    element: ReactElement<{ className: string }>;
    count: number;
    label: string;
  },
) {
  const { label, element, count, theme } = props;

  return (
    <div
      className={header.row({ theme })}
      title={label}
      aria-label={`${label}. Current value: ${count}`}
    >
      {Array.from({ length: count }).map((_, index) => {
        return cloneElement(element, { key: index, className: header.item });
      })}
    </div>
  );
}

export function DamageLimit(props: HeaderVariants & { value: number }) {
  const { value, theme = "red" } = props;

  return (
    <StatIndicator
      label="Damage limit"
      theme={theme}
      count={value}
      element={
        <svg
          width="17"
          height="15"
          viewBox="0 0 17 15"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M1.32031 7.75997L8.5 15L15.6797 7.75997C16.5251 6.90749 17 5.75128 17 4.54569V4.34179C17 1.94389 15.0723 0 12.6944 0C11.3864 0 10.1494 0.599561 9.33227 1.62949L8.5 2.67857L7.66773 1.62949C6.85065 0.599561 5.61359 0 4.30561 0C1.92769 0 0 1.94389 0 4.34179V4.54569C0 5.75128 0.474927 6.90749 1.32031 7.75997Z" />
        </svg>
      }
    />
  );
}

export function MomentumLimit(props: HeaderVariants & { value: number }) {
  const { value, theme = "green" } = props;

  return (
    <StatIndicator
      label="Momentum limit"
      theme={theme}
      count={value}
      element={
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M7.5 0C11.6421 0 15 3.35786 15 7.5C15 11.6421 11.6421 15 7.5 15C3.35786 15 0 11.6421 0 7.5C0 3.35786 3.35786 0 7.5 0ZM7.5 4C5.567 4 4 5.567 4 7.5C4 9.433 5.567 11 7.5 11C9.433 11 11 9.433 11 7.5C11 5.567 9.433 4 7.5 4Z" />
        </svg>
      }
    />
  );
}

export function KnightView() {
  const result = useKnightResult();
  const isReadOnly = useIsReadOnly();

  if (result.isFailure) {
    return <FailureView failure={result.failure} />;
  }

  const { value: knight } = result;
  return (
    <KnightContext value={knight}>
      <div className={view.container}>
        <div>
          <Heading className={header.heading}>{knight.name}</Heading>
          <div className={header.limits}>
            <DamageLimit value={knight.damageLimit} />
            <MomentumLimit value={knight.momentumLimit} />
          </div>
        </div>

        <div>
          <img src={dividerSrc} alt="" className={header.divider} />

          <KnightIssues />
        </div>

        <KnightSummary />

        <KnightEquipment />

        {!isReadOnly && (
          <DialogTrigger>
            <EditKnightDialog
              backTo={href("~/builder/w/:warbandId/k/:knightId", {
                path: { warbandId: knight.warband.id, knightId: knight.id },
              })}
            />
            <DialogDisclosure className={view.action}>Edit</DialogDisclosure>
          </DialogTrigger>
        )}
      </div>
    </KnightContext>
  );
}
